import { useEffect, useState } from 'react'
import { Card, Col, Empty, List, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { maintenanceDashboardApi, type ReportFilters } from '@/features/maintenance/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

/**
 * Spec §3 — Plant Head executive view.
 * Light. Numbers + light lists. Heavy drill-downs route to the report pages.
 */
export default function PlantHeadMaintenanceDashboardPage() {
  const { plantId } = usePlantId()
  const { machineCode, deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().startOf('month').format('YYYY-MM-DD'),
    to_date:   dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['plant-head-dashboard', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceDashboardApi.plantHead(filters),
  })

  const n = (k: string) => Number((data as Record<string, unknown>)?.[k] ?? 0)
  const obj = (k: string) => (data as Record<string, unknown>)?.[k] as Record<string, unknown> | undefined

  const downtimeTrend = (data as Record<string, unknown>)?.downtime_trend as Record<string, number> | undefined
  const costTrend = (data as Record<string, unknown>)?.cost_trend as Record<string, number> | undefined
  const trendRows = downtimeTrend
    ? Object.entries(downtimeTrend).map(([d, dt]) => ({
        date: d, downtime: dt, cost: costTrend?.[d] ?? 0,
      }))
    : []

  const topCost = obj('top_cost_machine') as { machine_id: string | null; total_cost: number } | undefined
  const topDt = obj('top_downtime_department') as { department_id: string | null; downtime_minutes: number } | undefined

  return (
    <div>
      <PageHeader
        title="Plant Head — Maintenance"
        subtitle="Executive summary of downtime, cost, PM compliance, and safety pressure."
      />

      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        onRefresh={() => refetch()}
        show={{ dateRange: true }}
      />

      {isLoading ? (
        <Card><Text type="secondary">Loading…</Text></Card>
      ) : !data ? <Empty /> : (
        <>
          {/* Headline cards */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={8} xl={6}>
              <Card><Statistic title="Total downtime (min)" value={Object.values(downtimeTrend ?? {}).reduce((a, b) => a + b, 0)} /></Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card><Statistic title="Maintenance cost" value={n('total_maintenance_cost')} prefix="₹" precision={2} /></Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card>
                <Statistic
                  title="PM compliance %"
                  value={n('pm_compliance_pct')}
                  suffix="%"
                  valueStyle={{ color: n('pm_compliance_pct') < 80 ? '#cf1322' : '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card><Statistic title="MTTR (min)" value={n('mttr_minutes')} /></Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card><Statistic title="MTBF (h)" value={n('mtbf_hours')} /></Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card>
                <Statistic
                  title="Emergency %"
                  value={n('emergency_percent')}
                  suffix="%"
                  valueStyle={{ color: n('emergency_percent') > 25 ? '#fa541c' : undefined }}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card><Statistic title="Repeat breakdowns" value={n('repeat_breakdown_count')} /></Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card>
                <Statistic
                  title="Critical machines"
                  value={n('critical_machine_count')}
                  valueStyle={{ color: n('critical_machine_count') > 0 ? '#cf1322' : undefined }}
                />
              </Card>
            </Col>
          </Row>

          {/* Phase 3.1 — Cost per kg headline */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={8} xl={6}>
              <Card>
                <Statistic
                  title="Total production (kg)"
                  value={(data as Record<string, unknown>)?.total_production_kg as number | null ?? '—'}
                  precision={(data as Record<string, unknown>)?.total_production_kg == null ? 0 : 2}
                />
              </Card>
            </Col>
            <Col xs={12} md={8} xl={6}>
              <Card>
                <Statistic
                  title="Maintenance cost / kg"
                  value={(data as Record<string, unknown>)?.maintenance_cost_per_kg as number | null ?? '—'}
                  prefix={(data as Record<string, unknown>)?.maintenance_cost_per_kg == null ? '' : '₹'}
                  precision={(data as Record<string, unknown>)?.maintenance_cost_per_kg == null ? 0 : 4}
                />
                <Tag color="geekblue" style={{ marginTop: 4 }}>
                  {(data as Record<string, unknown>)?.cost_per_kg_match_level as string ?? 'NONE'}
                </Tag>
              </Card>
            </Col>
          </Row>

          {/* Cost split */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6}><Card><Statistic title="Spare cost"   value={n('spare_cost_total')} prefix="₹" precision={2} /></Card></Col>
            <Col xs={12} md={6}><Card><Statistic title="Labour cost"  value={n('labour_cost_total')} prefix="₹" precision={2} /></Card></Col>
            <Col xs={12} md={6}><Card><Statistic title="Vendor cost"  value={n('vendor_cost_total')} prefix="₹" precision={2} /></Card></Col>
            <Col xs={12} md={6}><Card><Statistic title="Downtime cost" value={n('downtime_cost_total')} prefix="₹" precision={2} /></Card></Col>
          </Row>

          {/* Top machine / dept + planned vs unplanned */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={8}>
              <Card size="small" title="Top cost machine">
                {topCost?.machine_id
                  ? <Space direction="vertical">
                      <Text strong>{machineCode(topCost.machine_id)}</Text>
                      <Text>₹ {topCost.total_cost.toFixed(2)}</Text>
                    </Space>
                  : <Text type="secondary">No cost recorded in this window.</Text>}
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title="Top downtime department">
                {topDt?.department_id
                  ? <Space direction="vertical">
                      <Text strong>{deptName(topDt.department_id)}</Text>
                      <Text>{topDt.downtime_minutes} min</Text>
                    </Space>
                  : <Text type="secondary">No downtime in this window.</Text>}
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title="Planned vs Unplanned">
                <Space direction="vertical">
                  <Text>Planned: <Text strong>{n('planned_wo_count')}</Text></Text>
                  <Text>Unplanned: <Text strong>{n('unplanned_wo_count')}</Text></Text>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Trend table (cards keep the bundle light — no chart lib yet) */}
          <Card title="Daily downtime + cost trend" size="small">
            {trendRows.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              : <List
                  size="small"
                  dataSource={trendRows}
                  renderItem={(r) => (
                    <List.Item>
                      <Text>{dayjs(r.date).format('DD MMM')}</Text>
                      <Space split="·">
                        <Text type="secondary">Downtime: <Text strong>{r.downtime}</Text> min</Text>
                        <Text type="secondary">Cost: <Text strong>₹ {r.cost.toFixed(0)}</Text></Text>
                      </Space>
                    </List.Item>
                  )}
                />}
          </Card>

          <Card size="small" style={{ marginTop: 12 }} title="Drill-downs">
            <Space wrap>
              <Link to="/maintenance/reports/machine-downtime">Machine downtime</Link>
              <Link to="/maintenance/reports/department-breakdown">Department breakdown</Link>
              <Link to="/maintenance/reports/cost">Maintenance cost</Link>
              <Link to="/maintenance/reports/pm-compliance">PM compliance</Link>
              <Link to="/maintenance/reports/repeat-breakdown">Repeat breakdown</Link>
              <Link to="/maintenance/reports/machine-health">Machine health</Link>
            </Space>
          </Card>
        </>
      )}
    </div>
  )
}
